import { Card, CardBody } from './Card.js'
import { Field, Input, Select, Textarea, Checkbox } from './Field.js'
import { Section } from './Section.js'
import { ImageUpload } from './ImageUpload.js'
import { Button } from './Button.js'

export const EXAM_BOARDS = ['icse', 'ssc', 'cbse', 'state_board', 'other'] as const
export type ExamBoard = (typeof EXAM_BOARDS)[number]

export interface HistoryDraft {
  subject: string
  percentage: string
  proofUrl: string | null
}

export interface StudentFormValues {
  fullName: string
  dateOfBirth: string
  schoolName: string
  examBoard: ExamBoard | ''
  address: string
  photoUrl: string | null
  specialNotes: string
}

interface ClassOption {
  id: string
  name: string
  isActive: boolean
}

interface StudentFormProps {
  values: StudentFormValues
  onChange: (patch: Partial<StudentFormValues>) => void
  classes: ClassOption[]
  selectedClassIds: Set<string>
  onToggleClass: (id: string) => void
  history: HistoryDraft[]
  onHistoryChange: (next: HistoryDraft[]) => void
}

const blankHistory = (): HistoryDraft => ({ subject: '', percentage: '', proofUrl: null })

export function StudentForm({
  values,
  onChange,
  classes,
  selectedClassIds,
  onToggleClass,
  history,
  onHistoryChange,
}: StudentFormProps) {
  const activeClasses = classes.filter((c) => c?.isActive)

  const updateHistory = (i: number, patch: Partial<HistoryDraft>) => {
    onHistoryChange(history.map((h, idx) => (idx === i ? { ...h, ...patch } : h)))
  }

  const removeHistory = (i: number) => {
    onHistoryChange(history.filter((_, idx) => idx !== i))
  }

  return (
    <>
      <Card>
        <CardBody className="space-y-4">
          <Field label="Full name" required>
            <Input
              value={values.fullName}
              onChange={(e) => onChange({ fullName: e.target.value })}
              required
            />
          </Field>

          <div className="space-y-2">
            <p className="text-sm font-medium text-fg">
              Classes <span className="text-xs font-normal text-danger">*</span>
            </p>
            {activeClasses.length === 0 ? (
              <p className="text-sm text-fg-subtle italic">
                No active classes yet. Create one in Classes first, then assign from here.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 border-t border-border pt-3">
                {activeClasses.map((c) => (
                  <Checkbox
                    key={c.id}
                    label={c.name}
                    checked={selectedClassIds.has(c.id)}
                    onChange={() => onToggleClass(c.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Exam board" required>
              <Select
                value={values.examBoard}
                onChange={(e) => onChange({ examBoard: e.target.value as ExamBoard | '' })}
                required
              >
                <option value="" disabled>
                  Select a board
                </option>
                {EXAM_BOARDS.map((b) => (
                  <option key={b} value={b}>
                    {b.toUpperCase().replace('_', ' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Date of birth" required>
              <Input
                type="date"
                value={values.dateOfBirth}
                onChange={(e) => onChange({ dateOfBirth: e.target.value })}
                required
              />
            </Field>
            <Field label="School name" required>
              <Input
                value={values.schoolName}
                onChange={(e) => onChange({ schoolName: e.target.value })}
                required
              />
            </Field>
            <Field label="Address" required>
              <Input
                value={values.address}
                onChange={(e) => onChange({ address: e.target.value })}
                required
              />
            </Field>
          </div>

          <Field label="Photo" optional hint="Take a photo or choose one from your device.">
            <ImageUpload
              value={values.photoUrl}
              onChange={(url) => onChange({ photoUrl: url })}
              label="Add photo"
            />
          </Field>

          <Field
            label="Special notes"
            optional
            hint="Parent-agreed focus areas or anything the academy should keep in mind."
          >
            <Textarea
              value={values.specialNotes}
              onChange={(e) => onChange({ specialNotes: e.target.value })}
              rows={3}
            />
          </Field>
        </CardBody>
      </Card>

      <Section
        title="Academic history"
        description="Previous school results, with optional report-card image proof."
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onHistoryChange([...history, blankHistory()])}
          >
            Add result
          </Button>
        }
      >
        {history.length === 0 ? (
          <p className="text-sm text-fg-subtle">No results added yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((h, i) => (
              <Card key={i}>
                <CardBody className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                    <Field label="Subject" required>
                      <Input
                        value={h.subject}
                        onChange={(e) => updateHistory(i, { subject: e.target.value })}
                        placeholder="e.g. Mathematics"
                      />
                    </Field>
                    <Field label="Percentage" required>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={h.percentage}
                        onChange={(e) => updateHistory(i, { percentage: e.target.value })}
                        numeric
                      />
                    </Field>
                  </div>
                  <Field label="Report-card proof" optional>
                    <ImageUpload
                      value={h.proofUrl}
                      onChange={(url) => updateHistory(i, { proofUrl: url })}
                      label="Add proof image"
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() => removeHistory(i)}
                    className="text-xs text-fg-muted hover:text-danger"
                  >
                    Remove result
                  </button>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </Section>
    </>
  )
}
